import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useInView, useReducedMotion } from "motion/react";
import { EASE_EXPO } from "@/components/motion/primitives";
import { C, PauseButton, type SceneChip } from "./parts";

/*
 * One rounded frame that moves through a few pictures, Wise style. Each
 * picture slides up into place while the bar at the bottom rolls to its own
 * line, so picture and words always change together. It advances only while
 * on screen, the pause button stops it, the segments at the top jump to a
 * picture and reduced motion shows the first one, still.
 */

export interface Slide {
  id: string;
  /** A photo path, or a piece of code (the app on a desk). */
  photo?: { src: string; alt: string; position?: string };
  scene?: React.ReactNode;
  chip: SceneChip;
}

const DWELL = 5200;
const SLIDE = { duration: 0.95, ease: EASE_EXPO };

export function SlideFrame({ slides, label }: { slides: Slide[]; label: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { margin: "-20% 0px" });
  const reduced = useReducedMotion() ?? false;
  const [paused, setPaused] = useState(false);
  const [index, setIndex] = useState(0);
  const live = inView && !paused && !reduced;

  useEffect(() => {
    if (!live) return;
    const t = window.setTimeout(() => setIndex((i) => (i + 1) % slides.length), DWELL);
    return () => window.clearTimeout(t);
  }, [live, index, slides.length]);

  const slide = slides[index];

  return (
    <figure ref={ref} className="relative isolate w-full overflow-hidden bg-[#E9E6DF]" style={{ aspectRatio: "4 / 5", borderRadius: 40 }} aria-roledescription="carousel" aria-label={label}>
      <AnimatePresence initial={false}>
        <motion.div
          key={slide.id}
          className="absolute inset-0 overflow-hidden"
          initial={{ y: "100%" }}
          animate={{ y: "0%" }}
          exit={{ y: "-30%", opacity: 0.4 }}
          transition={SLIDE}
          style={{ zIndex: 1 }}
        >
          {slide.photo ? (
            <motion.img
              src={slide.photo.src}
              alt={slide.photo.alt}
              width={1122}
              height={1402}
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover"
              style={{ objectPosition: slide.photo.position ?? "50% 50%" }}
              initial={{ scale: 1.04 }}
              animate={{ scale: live ? 1 : 1.02 }}
              transition={{ duration: live ? DWELL / 1000 + 1.5 : 0.8, ease: "linear" }}
            />
          ) : (
            slide.scene
          )}
        </motion.div>
      </AnimatePresence>

      <PauseButton paused={paused} onToggle={() => setPaused((p) => !p)} hidden={reduced || slides.length < 2} />

      {/* The bar: the icon swaps and the words roll up with the picture. */}
      <div
        className="absolute inset-x-[5%] bottom-[4.5%] z-10 flex items-center gap-3 overflow-hidden rounded-full bg-white py-2.5 pl-2.5 pr-5 shadow-[0_10px_30px_rgba(10,15,14,0.16)] sm:gap-4 sm:py-3 sm:pl-3 sm:pr-6"
        aria-live="polite"
      >
        <span className="relative flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full sm:h-14 sm:w-14" style={{ backgroundColor: C.TEAL, color: C.BRAND_DARK }}>
          <AnimatePresence mode="popLayout" initial={false}>
            <motion.span key={slide.id} initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -30, opacity: 0 }} transition={SLIDE}>
              {slide.chip.icon}
            </motion.span>
          </AnimatePresence>
        </span>
        <span className="relative h-[1.5em] min-w-0 flex-1 overflow-hidden text-[16px] font-bold tracking-[-0.01em] sm:text-[20px]" style={{ color: C.INK }}>
          <AnimatePresence mode="popLayout" initial={false}>
            <motion.span key={slide.id} className="absolute inset-0 truncate" initial={{ y: "110%" }} animate={{ y: "0%" }} exit={{ y: "-110%" }} transition={SLIDE}>
              {slide.chip.label}
            </motion.span>
          </AnimatePresence>
        </span>
        <span className="relative h-[1.5em] shrink-0 overflow-hidden text-right text-[16px] font-extrabold tabular-nums tracking-[-0.01em] sm:text-[20px]" style={{ color: C.TEAL_DEEP }}>
          <AnimatePresence mode="popLayout" initial={false}>
            <motion.span key={slide.id} className="block whitespace-nowrap" initial={{ y: "110%" }} animate={{ y: "0%" }} exit={{ y: "-110%" }} transition={{ ...SLIDE, delay: 0.06 }}>
              {slide.chip.value}
            </motion.span>
          </AnimatePresence>
        </span>
      </div>
    </figure>
  );
}
